import React from 'react'
import { Button } from 'react-bootstrap'
import moment from 'moment'
import numeral from 'numeral'
import { cn } from '@/features/utils'
import { getMonthlyPriceFromStripePlan } from '../planConfig'

export const planIcons: Record<string, React.ReactNode> = {
  Free: (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="8" width="18" height="4" rx="1"></rect>
      <path d="M12 8v13"></path>
      <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7"></path>
      <path d="M7.5 8a2.5 2.5 0 0 1 0-5A4.8 8 0 0 1 12 8a4.8 8 0 0 1 4.5-5 2.5 2.5 0 0 1 0 5"></path>
    </svg>
  ),
  Entrepreneur: (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"></path>
      <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"></path>
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"></path>
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"></path>
    </svg>
  ),
  Business: (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
      <path d="M10 6h4"></path>
      <path d="M10 10h4"></path>
      <path d="M10 14h4"></path>
      <path d="M10 18h4"></path>
    </svg>
  ),
  'Legacy Business': (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2"></path>
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2"></path>
      <path d="M10 6h4"></path>
      <path d="M10 10h4"></path>
      <path d="M10 14h4"></path>
      <path d="M10 18h4"></path>
    </svg>
  ),
  Enterprise: (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/>
    </svg>
  ),
}

export interface PricingPlanOption {
  product: string
  priceMonth?: number | null
  description: Array<Array<string>>
  limit?: string
  tagline?: string
  priceLabel?: string
  aiCredits?: string
  icon?: string
}

export interface PricingPlanCardProps {
  option: PricingPlanOption
  onClick: () => void
  /** Billing page: matches active subscription */
  isCurrentPlan?: boolean
  currentPlan?: {
    product?: string
    amount?: number
    interval?: string
    interval_count?: number
    quantity?: number
    price?: number
    nickname?: string
    metadata?: Record<string, unknown>
  }
  fromShopify?: boolean
  isBusiness?: boolean
  businessPrice?: number
  subscriptionExpired?: boolean
  isTrialing?: boolean
  trialEnd?: number
  /** Wizard: selection UI instead of manage-plan */
  selectionMode?: boolean
  isSelected?: boolean
  hideDetailsLink?: boolean
  colClassName?: string
}

export default function PricingPlanCard({
  option,
  onClick,
  isCurrentPlan = false,
  currentPlan = {},
  fromShopify = false,
  isBusiness = false,
  businessPrice,
  subscriptionExpired = false,
  isTrialing = false,
  trialEnd,
  selectionMode = false,
  isSelected = false,
  hideDetailsLink = false,
  colClassName,
}: PricingPlanCardProps) {
  const { product, priceMonth: price, description, limit, tagline, priceLabel, aiCredits } = option
  const highlighted = selectionMode ? isSelected : isCurrentPlan

  let displayedPrice = price
  if (!selectionMode && isCurrentPlan && !fromShopify && currentPlan) {
    const currentPlanMonthlyPrice = getMonthlyPriceFromStripePlan(currentPlan)
    if (
      typeof currentPlanMonthlyPrice === 'number' &&
      !Number.isNaN(currentPlanMonthlyPrice) &&
      price !== currentPlanMonthlyPrice
    ) {
      displayedPrice = currentPlanMonthlyPrice
    }
  }
  if (fromShopify && isBusiness) {
    displayedPrice = 100
  }
  if (typeof displayedPrice === 'number' && Number.isNaN(displayedPrice)) {
    displayedPrice = null
  }
  const disabled = false
  const isFree = product === 'Free'
  const isEnterprise = product === 'Enterprise'
  const isEntrepreneur = product === 'Entrepreneur'

  const fmt = (n: number) => numeral(n).format('0,0')

  const renderPrice = () => {
    if (isEnterprise) {
      if (!selectionMode && isCurrentPlan) {
        const amt = (currentPlan?.amount || 0) / 100
        const per =
          currentPlan?.interval === 'year'
            ? currentPlan?.interval_count && currentPlan.interval_count > 1
              ? `${currentPlan.interval_count} yrs`
              : 'yr'
            : 'mo'
        return <><span className="price-amount">${fmt(amt)}</span><span className="price-period">/{per}</span></>
      }
      return <>
        <span className="price-amount" style={{fontSize: 28}}>Custom Pricing</span>
        <div className="pricing-card-price-label" style={{color: '#a0aec0'}}>Tailored to your scale</div>
      </>
    }
    if (isBusiness) {
      if (!selectionMode && isCurrentPlan) {
        if (fromShopify) return currentPlan?.interval === 'year'
          ? <><span className="price-amount">${fmt(Math.round((businessPrice || 0) * 9.6))}</span><span className="price-period">/year</span></>
          : <><span className="price-amount">${fmt(businessPrice || 0)}</span><span className="price-period">/30 days</span></>
        const amt = (currentPlan?.amount || 0) / 100
        const per =
          currentPlan?.interval === 'year'
            ? currentPlan?.interval_count && currentPlan.interval_count > 1
              ? `${currentPlan.interval_count} yrs`
              : 'yr'
            : 'mo'
        return <><span className="price-amount">${fmt(amt)}</span><span className="price-period">/{per}</span></>
      }
      return <><span className="price-amount">${fmt(businessPrice ?? displayedPrice ?? 0)}</span><span className="price-period">/{fromShopify ? '30 days' : 'mo'}</span></>
    }
    if (isEntrepreneur && !selectionMode && isCurrentPlan) {
      if (fromShopify) {
        return currentPlan?.interval === 'year'
          ? <><span className="price-amount">${fmt(Math.round((currentPlan?.price || 0) * 9.6))}</span><span className="price-period">/year</span></>
          : <><span className="price-amount">${fmt(Math.round(currentPlan?.price || 0))}</span><span className="price-period">/30 days</span></>
      }
      const perUserAmt = getMonthlyPriceFromStripePlan(currentPlan ?? {})
      const quantity = currentPlan?.quantity || 1
      return <><span className="price-amount">${fmt(perUserAmt * quantity)}</span><span className="price-period">/mo</span></>
    }
    if (isEntrepreneur) {
      return <>
        <span className="price-amount">${fmt(displayedPrice || 0)}</span>
        <span className="price-period">/{fromShopify ? '30 days' : 'mo per user'}</span>
      </>
    }
    return <>
      <span className="price-amount">${fmt(displayedPrice || 0)}</span>
      <span className="price-period">/{fromShopify ? '30 days' : 'mo'}</span>
    </>
  }

  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isEnterprise && !selectionMode) return
    if (!selectionMode && fromShopify && isCurrentPlan && !subscriptionExpired && product !== 'Entrepreneur') return
    if (!selectionMode && isFree && isCurrentPlan) return
    if (disabled) return
    const target = e.target as HTMLElement
    if (
      target.classList.contains('contact-btn') ||
      target.classList.contains('pricing-description-link')
    ) return
    onClick()
  }

  const renderCta = () => {
    if (selectionMode) {
      return highlighted ? (
        <Button className={`pricing-card-btn current-btn ${product}`}>
          <b>SELECTED</b>
        </Button>
      ) : (
        <Button className={`pricing-card-btn ${product}`} disabled={disabled}>
          <b>CHOOSE PLAN</b>
        </Button>
      )
    }
    if (isEnterprise) {
      return (
        <Button className={`pricing-card-btn ${product}`} onClick={(e) => { e.stopPropagation(); window.location.href = 'mailto:success@payhelm.com' }}>
          <b>CONTACT SALES</b>
        </Button>
      )
    }
    if (isCurrentPlan) {
      return (
        <Button className={`pricing-card-btn current-btn ${product}`}>
          <b>{fromShopify ? (isEntrepreneur ? 'MANAGE SEATS' : 'YOUR PLAN') : 'MANAGE PLAN'}</b>
        </Button>
      )
    }
    return (
      <Button className={`pricing-card-btn ${product}`} disabled={disabled}>
        <b>CHOOSE PLAN</b>
      </Button>
    )
  }

  return (
    <div className={cn('pricing-card-col', colClassName)}>
      <div
        className={`pricing-card ${product} ${highlighted ? 'current' : ''} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        onClick={handleCardClick}
      >
        {highlighted && !selectionMode && (
          <span className={`fa fa-star pricing-card-star ${product}`} />
        )}

        <div className={`pricing-card-icon ${product}`}>
          {planIcons[product] || <span className={`fa ${option.icon}`} />}
        </div>

        <div className="pricing-card-name">{product}</div>
        <p className="pricing-card-tagline">{tagline}</p>

        <div className="pricing-card-price">
          {renderPrice()}
        </div>
        {priceLabel && !isEntrepreneur && <div className="pricing-card-price-label">{priceLabel}</div>}
        {!selectionMode && isCurrentPlan && isEntrepreneur && !fromShopify && (
          <div className="pricing-card-price-label" style={{fontWeight: 600}}>
            {currentPlan.quantity || 1} user{(currentPlan.quantity || 1) !== 1 ? 's' : ''}
          </div>
        )}

        <hr className="pricing-card-divider" />

        {aiCredits && (
          <div className="pricing-card-ai-credits">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
              <path d="M20 3v4"/>
              <path d="M22 5h-4"/>
              <path d="M4 17v2"/>
              <path d="M5 18H3"/>
            </svg>
            {aiCredits}
          </div>
        )}

        <ul className="pricing-card-features">
          {description.map((line, lineIndex) => (
            <li key={`line-${lineIndex}`}>
              <span className={`fa fa-check pricing-card-check ${product}`} />
              <span>
                {line.map((words, wordsIndex) => (
                  <span key={`line-${lineIndex}-${wordsIndex}`}>
                    {words.startsWith('b_') ? <b>{words.substr(2)}</b> : words}
                  </span>
                ))}
              </span>
            </li>
          ))}
          {limit && (
            <li>
              <span className={`fa fa-check pricing-card-check ${product}`} />
              <span>{limit}</span>
            </li>
          )}
        </ul>

        <div style={{flexGrow: 1}} />

        {!selectionMode && isTrialing && !isFree && (
          <div style={{fontSize: 12, color: '#04a778', marginBottom: 8, textAlign: 'center'}}>
            <span className="fa fa-info-circle" style={{marginRight: 4}} />
            No charge until {moment(trialEnd).format('MMM D, YYYY')}
          </div>
        )}

        <div className="pricing-card-cta">
          {renderCta()}
        </div>

        {!hideDetailsLink && (
          <div className="pricing-card-link">
            <a href="https://www.payhelm.com/pricing/" className="pricing-description-link" target="_blank" rel="noreferrer">See all details</a>
          </div>
        )}
      </div>
    </div>
  )
}
